import { R4 } from "@ahryman40k/ts-fhir-types";

import {
  FHIR_EXT_ENTRY_CROSS_REF,
  FHIR_EXT_ENTRY_CROSS_REF_STU5,
  FHIR_EXT_REPLACED_BY,
} from "../constants";
import { VaccinationResource, vaccinationResourceTypes } from "../types";
import { getMetaExtension, getUuidUrnReferenceValue, setMetaExtension } from "./fhir";

export function addReplacedByExtensions(bundle: R4.IBundle) {
  bundle.entry?.forEach((entry) => {
    if (!entry.resource) {
      return;
    }

    if (!vaccinationResourceTypes.includes(entry.resource.resourceType)) {
      return;
    }

    const resource = entry.resource as VaccinationResource;
    const replacedResourceUuid = getReplacedEntryUuid(resource);

    if (replacedResourceUuid) {
      const replacedResource = bundle.entry?.find((entry) => {
        if (entry.resource && vaccinationResourceTypes.includes(entry.resource.resourceType)) {
          const vacResource = entry.resource as VaccinationResource;
          return vacResource.identifier?.some((id) => id.value === replacedResourceUuid);
        }
        return false;
      })?.resource as VaccinationResource | undefined;

      if (replacedResource) {
        setReplacedByExtension(replacedResource, resource);
      }
    }
  });
}

export function sortByDateAndVersion(resources: VaccinationResource[]): VaccinationResource[] {
  // Identify latest resources (values not appearing as keys)
  const latestRefs = resources
    .filter((resource) => !isReplaced(resource))
    .map((resource) => getUuidUrnReferenceValue(resource));

  // Map identifiers to resources for a quick lookup
  const resourceMap = new Map<string, VaccinationResource>();
  for (const resource of resources) {
    resourceMap.set(getUuidUrnReferenceValue(resource), resource);
  }

  // Sort latest IDs by date
  const latestRefsSorted = Array.from(latestRefs).sort((a, b) => {
    const resourceA = resourceMap.get(a) as VaccinationResource | null;
    const resourceB = resourceMap.get(b) as VaccinationResource | null;
    if (!resourceA && !resourceB) {
      return 0;
    } else if (!resourceB) {
      return -1;
    } else if (!resourceA) {
      return 1;
    }

    const dateA = getVaccinationResourceDate(resourceA);
    const dateB = getVaccinationResourceDate(resourceB);

    // Sort by date
    if (dateA && dateB) {
      return dateB.getTime() - dateA.getTime();
    } else if (dateA) {
      return -1;
    } else if (dateB) {
      return 1;
    }
    return 0;
  });

  // Traverse chains from latest to oldest
  const orderedRefs: string[] = [];
  for (const latestRef of latestRefsSorted) {
    let currentRef: string | undefined = latestRef;
    while (currentRef) {
      if (!orderedRefs.includes(currentRef)) {
        orderedRefs.push(currentRef);
      }
      const currentResource = resourceMap.get(currentRef) as VaccinationResource;
      const replacedResourceUuid = getReplacedEntryUuid(currentResource);
      const replacedResource = resources.find((res) =>
        res.identifier?.some((id) => id.value === replacedResourceUuid)
      );
      if (!replacedResource) {
        break;
      }
      currentRef = getUuidUrnReferenceValue(replacedResource);
    }
  }

  // Return resources in order based on identifier chain
  return orderedRefs
    .map((id) => resourceMap.get(id))
    .filter((res): res is VaccinationResource => !!res);
}

export function getVaccinationResourceDate(resource: VaccinationResource): Date | null {
  let date = null;
  if (resource.resourceType === "Immunization") {
    date = (resource as R4.IImmunization).occurrenceDateTime;
  } else if (["Condition", "AllergyIntolerance"].includes(resource.resourceType)) {
    date = (resource as R4.IAllergyIntolerance | R4.ICondition).recordedDate;
  } else if (resource.resourceType === "Observation") {
    date = (resource as R4.IObservation).effectiveDateTime;
  }

  return date ? new Date(date) : null;
}

function setReplacedByExtension(
  oldResource: VaccinationResource,
  newResource: VaccinationResource
) {
  setMetaExtension(oldResource, {
    url: FHIR_EXT_REPLACED_BY,
    valueReference: {
      reference: getUuidUrnReferenceValue(newResource),
    },
  });
}

export function isReplaced(resource: VaccinationResource): boolean {
  return !!getMetaExtension(resource, FHIR_EXT_REPLACED_BY);
}

export function getReplacesExtension(resource: VaccinationResource): R4.IExtension | undefined {
  return resource.extension?.find(
    (ext) =>
      ext.url === FHIR_EXT_ENTRY_CROSS_REF ||
      (ext.url === FHIR_EXT_ENTRY_CROSS_REF_STU5 &&
        ext.extension?.some((ext) => ext.url === "relationcode" && ext.valueCode === "replaces"))
  );
}

export function replaces(resource: VaccinationResource): boolean {
  return !!getReplacesExtension(resource);
}

export function getReplacedEntryUuid(resource: VaccinationResource): string | undefined {
  const replacesExtension = getReplacesExtension(resource);
  if (!replacesExtension) {
    return;
  }

  const entryReference = replacesExtension?.extension?.find((ext) => ext.url === "entry")
    ?.valueReference as R4.IReference | undefined;
  if (!entryReference) {
    return;
  }

  return entryReference.identifier?.value || entryReference.reference;
}

export function getReplacedCompositionRef(resource: VaccinationResource): string | undefined {
  const replacesExtension = getReplacesExtension(resource);
  const refObject = replacesExtension?.extension?.find(
    (ext) =>
      ext.url === (replacesExtension.url === FHIR_EXT_ENTRY_CROSS_REF ? "document" : "composition")
  )?.valueReference as R4.IReference | undefined;
  if (!refObject) {
    return;
  }

  return refObject.identifier?.value || refObject.reference;
}
